"use client"

import React, { Suspense } from "react"
import { useSearchParams } from "next/navigation"
import {
  DEFAULT_SEARCH_RESULT_PAGE_SIZES,
  SEARCH_PARAMS_FF_SEARCH_PARAMS,
  SEARCH_PARAMS_PAGE,
  SEARCH_PARAMS_PAGE_SIZE,
  SEARCH_PARAMS_PAGE_VIEW_OPTIONS,
} from "@/constants/ui"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { SearchParams } from "@ub/fact-finder-client"
import { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"

import type { MarketChannel } from "@/lib/market/market-channel"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { decodeObj } from "@/lib/urlObject"
import { cn } from "@/lib/utils"
import FallbackCard from "@/components/common/FallbackCard"
import ProductCardWrapper from "@/components/product/ProductCardWrapper"
import { BannerClient } from "@/components/search-and-category/BannerClient"

export default function SearchResultContainerGridList(props: {
  banner: string | undefined
  bannerPosition: number | undefined
  bannerCampaignId: string | undefined
  products: ReducedProductData[]
  sbLocale?: string
  channel?: MarketChannel
  isoLocale: string
  tracking: string
  templateBlok: any
  isB2B: boolean
  allowDebugMode: boolean | undefined
  hideAddToBasket: boolean | undefined
  inStoryBlok: boolean | undefined
  forceListView?: boolean
  categoryPath?: string[]
  searchTerm?: string
  campaignId?: string
}) {
  const { articleListView } = useUbStorefrontState()

  const isListView = props.forceListView || articleListView == SEARCH_PARAMS_PAGE_VIEW_OPTIONS[1]
  const isBannerPosition = (index: number) =>
    props.banner !== undefined && props.bannerPosition !== undefined && index === props.bannerPosition - 1
  const isBannerAtLastPosition =
    props.banner !== undefined && props.bannerPosition !== undefined && props.bannerPosition > props.products.length
  const searchParams = useSearchParams()
  const ffSearchParamsEncrypted = searchParams.get(SEARCH_PARAMS_FF_SEARCH_PARAMS) ?? undefined
  const pageParam = Number(searchParams.get(SEARCH_PARAMS_PAGE) ?? 1)
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1
  const pageSizeParam = Number(searchParams.get(SEARCH_PARAMS_PAGE_SIZE) ?? DEFAULT_SEARCH_RESULT_PAGE_SIZES[0])
  const pageSize =
    Number.isFinite(pageSizeParam) && pageSizeParam > 0 ? pageSizeParam : DEFAULT_SEARCH_RESULT_PAGE_SIZES[0]
  const offset = (page - 1) * pageSize
  const advisorSelectionActive = (() => {
    if (!ffSearchParamsEncrypted) {
      return false
    }

    try {
      return !!(decodeObj(ffSearchParamsEncrypted) as SearchParams | undefined)?.advisorStatus
    } catch {
      return false
    }
  })()
  const determineCampaignId = (productData: ReducedProductData) => {
    if (!advisorSelectionActive && props.campaignId && productData.ffCampaignId === props.campaignId) {
      return undefined
    }

    return productData.ffCampaignId || (advisorSelectionActive ? props.campaignId : undefined)
  }

  return (
    <>
      <div
        className={cn("grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4", {
          "@lg:auto-rows-[minmax(1fr,auto)] @lg:grid-cols-1!": isListView,
        })}
      >
        {props.products.map((productData, index) => (
          <Suspense key={productData.id} fallback={<FallbackCard className={"bg-gray-100"} />}>
            <>
              {isBannerPosition(index) && (
                <BannerClient
                  bannerContent={props.banner ?? ""}
                  isListView={isListView}
                  bannerCampaignId={props.bannerCampaignId}
                  bannerPosition={props.bannerPosition}
                />
              )}
              <ProductCardWrapper
                productData={productData}
                effectivePrices={productData.effectivePrices as ProductPriceContainerCtnrROV1}
                view={props.forceListView ? SEARCH_PARAMS_PAGE_VIEW_OPTIONS[1] : articleListView}
                cardVariant={props.templateBlok.variant ?? "standard"}
                isB2B={props.isB2B}
                showEcoTax={props.templateBlok.showEcoTax ?? false}
                debugMode={props.templateBlok.debugMode && props.allowDebugMode}
                hideAddToBasket={props.hideAddToBasket ?? false}
                priceRequestLink={""}
                sbLocale={props.sbLocale}
                channel={props.channel}
                isoLocale={props.isoLocale}
                trackingList={props.tracking ?? "direct"}
                position={offset + index + 1}
                mainProductId={undefined}
                isRecommendation={props.templateBlok.isRecommendation}
                inStoryBlock={props.inStoryBlok}
                ffSearchParamsEncrypted={ffSearchParamsEncrypted}
                categoryPath={props.categoryPath}
                searchTerm={props.searchTerm}
                campaignId={determineCampaignId(productData)}
              />
            </>
          </Suspense>
        ))}
        {isBannerAtLastPosition && (
          <BannerClient
            bannerContent={props.banner ?? ""}
            isListView={isListView}
            bannerCampaignId={props.bannerCampaignId}
            bannerPosition={props.bannerPosition}
          />
        )}
      </div>
    </>
  )
}
